import {
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { Inventory } from "../types";

interface InventoryDetailsDialogProps {
  open: boolean;
  inventory: Inventory | null;
  onClose: () => void;
}

const money = (value?: number) => `Rs ${Number(value || 0).toLocaleString()}`;

const Detail = ({ label, value }: { label: string; value?: string | number }) => (
  <Stack spacing={0.5}>
    <Typography variant="caption" color="text.secondary" fontWeight={800}>
      {label.toUpperCase()}
    </Typography>
    <Typography fontWeight={700}>{value ?? "-"}</Typography>
  </Stack>
);

const InventoryDetailsDialog = ({
  open,
  inventory,
  onClose,
}: InventoryDetailsDialogProps) => {
  if (!inventory) return null;

  const hasReservedStock = inventory.reservedStock > 0;
  const hasDamagedStock = inventory.damagedStock > 0;
  const hasMinimumStock = inventory.minimumStock > 0;
  const hasMaximumStock = (inventory.maximumStock ?? 0) > 0;
  const hasLastMovement = Boolean(inventory.lastUpdated && inventory.lastUpdated !== "-");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack>
            <Typography variant="h6" fontWeight={900}>
              {inventory.sparePartName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {inventory.partCode} • {inventory.categoryName}
            </Typography>
          </Stack>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Detail label="Warehouse" value={inventory.warehouseName || inventory.warehouseId} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Detail label="Stock Code" value={inventory.code} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary" fontWeight={800}>
                STATUS
              </Typography>
              <Chip
                size="small"
                color={inventory.status === "ACTIVE" ? "success" : "default"}
                label={inventory.status}
                sx={{ width: "fit-content", fontWeight: 800 }}
              />
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={6} md={3}>
            <Detail label="Available" value={inventory.availableStock} />
          </Grid>
          {hasReservedStock && (
            <Grid item xs={6} md={3}>
              <Detail label="Reserved" value={inventory.reservedStock} />
            </Grid>
          )}
          {hasDamagedStock && (
            <Grid item xs={6} md={3}>
              <Detail label="Damaged" value={inventory.damagedStock} />
            </Grid>
          )}
          <Grid item xs={6} md={3}>
            <Detail label="Total" value={inventory.totalStock} />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={6} md={3}>
            <Detail label="Reorder Level" value={inventory.reorderLevel} />
          </Grid>
          {hasMinimumStock && (
            <Grid item xs={6} md={3}>
              <Detail label="Minimum Stock" value={inventory.minimumStock} />
            </Grid>
          )}
          {hasMaximumStock && (
            <Grid item xs={6} md={3}>
              <Detail label="Maximum Stock" value={inventory.maximumStock ?? undefined} />
            </Grid>
          )}
          {hasLastMovement && (
            <Grid item xs={6} md={3}>
              <Detail label="Last Movement" value={inventory.lastUpdated} />
            </Grid>
          )}

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12} md={4}>
            <Detail label="Valuation Method" value={inventory.valuationMethod || "WEIGHTED_AVERAGE"} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Detail label="Average Unit Cost" value={money(inventory.weightedAverageUnitCost)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Detail label="Inventory Value" value={money(inventory.inventoryValue)} />
          </Grid>

          {inventory.remarks && (
            <Grid item xs={12}>
              <Detail label="Remarks" value={inventory.remarks} />
            </Grid>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default InventoryDetailsDialog;
